import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Eye, MousePointer, DollarSign, Target, TrendingUp, Monitor, Smartphone, Tablet, Calendar } from 'lucide-react';
import { campaignService } from '../../services/campaignService';
import { Campaign } from '../../types';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

interface CampaignAnalyticsProps {
  campaignId: string;
  campaign: Campaign;
}

// Helpers pra formatar Date como YYYY-MM-DD (o backend espera esse formato)
const toDateInput = (d: Date) => d.toISOString().split('T')[0];
const daysAgo = (n: number) => toDateInput(new Date(Date.now() - n * 86400000));

const CampaignAnalytics: React.FC<CampaignAnalyticsProps> = ({ campaignId, campaign }) => {
  const { t } = useTranslation();
  // Preset rápido (7d/30d/90d/custom) + datas controladas. Inicializa em últimos 30 dias.
  const [preset, setPreset] = useState<'7d' | '30d' | '90d' | 'custom'>('30d');
  const [startDate, setStartDate] = useState<string>(daysAgo(30));
  const [endDate, setEndDate] = useState<string>(toDateInput(new Date()));
  const [metrics, setMetrics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Aplica preset (muda datas); custom deixa o user editar livremente
  useEffect(() => {
    if (preset === 'custom') return;
    const days = preset === '7d' ? 7 : preset === '90d' ? 90 : 30;
    setStartDate(daysAgo(days));
    setEndDate(toDateInput(new Date()));
  }, [preset]);

  useEffect(() => {
    if (!startDate || !endDate) return;
    loadMetrics(startDate, endDate);
  }, [campaignId, startDate, endDate]);

  const loadMetrics = async (from: string, to: string) => {
    setIsLoading(true);
    try {
      const res = await campaignService.getCampaignMetrics(campaignId, from, to);
      if (res.success) setMetrics(res.data);
    } catch (error) {
      console.error('Error loading metrics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value: number) =>
    `R$ ${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

  // Honesto ao período: usa SÓ o que veio do backend filtrado. Sem fallback pro lifetime do campaign,
  // que mascarava os KPIs mostrando valores acumulados quando o período tinha 0.
  const m = metrics || {};
  const impressions = m.impressions || 0;
  const clicks = m.clicks || 0;
  const spend = m.spend || 0;
  const conversions = m.conversions || 0;
  // CTR/CPC vêm calculados do backend — não recalculamos no front pra evitar divergência
  // quando o provider entrega valores já calculados diferentes da soma manual.
  const ctr = typeof m.ctr === 'number' ? m.ctr : 0;
  const cpc = typeof m.cpc === 'number' ? m.cpc : 0;

  const kpis = [
    { label: t('campaigns.metrics.impressions', 'Impressões'), value: impressions.toLocaleString('pt-BR'), icon: Eye, color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/30' },
    { label: t('campaigns.metrics.clicks', 'Cliques'), value: clicks.toLocaleString('pt-BR'), icon: MousePointer, color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/30' },
    { label: 'CTR', value: `${(ctr * 100).toFixed(2)}%`, icon: TrendingUp, color: 'text-purple-600', bg: 'bg-purple-100 dark:bg-purple-900/30' },
    { label: 'CPC', value: formatCurrency(cpc), icon: DollarSign, color: 'text-orange-600', bg: 'bg-orange-100 dark:bg-orange-900/30' },
    { label: t('campaigns.metrics.conversions', 'Conversões'), value: conversions.toLocaleString('pt-BR'), icon: Target, color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
    { label: t('campaigns.metrics.spend', 'Gasto'), value: formatCurrency(spend), icon: BarChart3, color: 'text-indigo-600', bg: 'bg-indigo-100 dark:bg-indigo-900/30' },
  ];

  // ROAS só é significativo com receita por conversão, que o schema atual não guarda.
  // Mostramos o ROAS lifetime do campaign num bloco separado abaixo, rotulado, em vez de
  // fingir que é do período (comportamento antigo que misturava lifetime com filtro).
  const lifetimeRoas = typeof campaign.roas === 'number' && campaign.roas > 0 ? campaign.roas : null;

  const platformLabels: Record<string, string> = {
    google: 'Google Ads',
    meta: 'Meta Ads',
    linkedin: 'LinkedIn Ads'
  };

  const deviceIcons: Record<string, React.ReactNode> = {
    desktop: <Monitor className="w-5 h-5" />,
    mobile: <Smartphone className="w-5 h-5" />,
    tablet: <Tablet className="w-5 h-5" />,
  };

  const deviceLabels: Record<string, string> = {
    desktop: 'Desktop',
    mobile: 'Mobile',
    tablet: 'Tablet',
    other: 'Outro',
  };

  return (
    <div className="space-y-4 sm:space-y-6 min-w-0">
      {/* Filtro de período: presets rápidos + intervalo customizado (de/até). */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="grid grid-cols-4 sm:flex items-center gap-1 sm:gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-full sm:w-auto">
          {(['7d', '30d', '90d', 'custom'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setPreset(range)}
              className={`px-1.5 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap truncate min-w-0 ${
                preset === range
                  ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {range === '7d' ? t('common.days7', '7 dias')
                : range === '30d' ? t('common.days30', '30 dias')
                : range === '90d' ? t('common.days90', '90 dias')
                : t('common.custom', 'Personalizado')}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 text-sm w-full sm:w-auto min-w-0">
          <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => { setPreset('custom'); setStartDate(e.target.value); }}
            className="flex-1 sm:flex-none min-w-0 px-2 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            aria-label={t('common.startDate', 'Data inicial')}
          />
          <span className="text-gray-400 flex-shrink-0">—</span>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={toDateInput(new Date())}
            onChange={(e) => { setPreset('custom'); setEndDate(e.target.value); }}
            className="flex-1 sm:flex-none min-w-0 px-2 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            aria-label={t('common.endDate', 'Data final')}
          />
        </div>
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
          {t('campaigns.analytics.loadingMetrics', 'Carregando métricas...')}
        </div>
      )}

      {/* KPI Cards */}
      <div className={`grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-4 transition-opacity ${isLoading ? 'opacity-50' : 'opacity-100'}`}>
        {kpis.map((kpi, idx) => {
          const Icon = kpi.icon;
          return (
            <div key={idx} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 sm:p-4 min-w-0">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 ${kpi.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${kpi.color}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate" title={String(kpi.value)}>{kpi.value}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{kpi.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Performance Chart */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 sm:p-6 min-w-0">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('campaigns.analyticsHub.performanceChart', 'Performance')}
        </h3>
        <div className="h-64 sm:h-80 min-w-0">
          {metrics?.daily && metrics.daily.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={metrics.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
                <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} minTickGap={16} />
                <YAxis yAxisId="left" stroke="#9ca3af" fontSize={11} width={40} />
                <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" fontSize={11} width={40} />
                <Tooltip contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', border: 'none', borderRadius: '8px', color: '#fff' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line yAxisId="left" type="monotone" dataKey="clicks" stroke="#5b7bf1" strokeWidth={2} name={t('campaigns.metrics.clicks', 'Cliques')} dot={false} />
                <Line yAxisId="left" type="monotone" dataKey="impressions" stroke="#d946ef" strokeWidth={2} name={t('campaigns.metrics.impressions', 'Impressões')} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="spend" stroke="#f59e0b" strokeWidth={2} name={t('campaigns.metrics.spend', 'Gasto')} dot={false} />
                <Line yAxisId="left" type="monotone" dataKey="conversions" stroke="#10b981" strokeWidth={2} name={t('campaigns.metrics.conversions', 'Conversões')} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              {isLoading ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
              ) : (
                t('common.noData', 'Sem dados')
              )}
            </div>
          )}
        </div>
      </div>

      {/* Platform Breakdown */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('campaigns.analyticsHub.platformBreakdown', 'Desempenho por Plataforma')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {metrics?.platformBreakdown && metrics.platformBreakdown.length > 0 ? (
            metrics.platformBreakdown.map((pb: any) => (
              <div key={pb.platform} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="font-medium text-gray-900 dark:text-white mb-2">{platformLabels[pb.platform] || pb.platform}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('campaigns.metrics.impressions', 'Impressões')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{(pb.impressions || 0).toLocaleString('pt-BR')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('campaigns.metrics.clicks', 'Cliques')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{(pb.clicks || 0).toLocaleString('pt-BR')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('campaigns.metrics.spend', 'Gasto')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(pb.spend || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('campaigns.metrics.conversions', 'Conversões')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{(pb.conversions || 0).toLocaleString('pt-BR')}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            (campaign.platforms || [campaign.platform]).filter(p => p !== 'both' && p !== 'all').map((platform) => (
              <div key={platform} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="font-medium text-gray-900 dark:text-white capitalize mb-2">{platformLabels[platform] || platform}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('campaigns.metrics.spend', 'Gasto')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(campaign.total_spent || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('campaigns.metrics.clicks', 'Cliques')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{(campaign.total_clicks || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ROAS (lifetime — schema atual não guarda receita por período) */}
      {lifetimeRoas !== null && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">ROAS <span className="uppercase tracking-wide">lifetime</span></p>
            <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{lifetimeRoas.toFixed(1)}x</p>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 sm:max-w-xs sm:text-right">
            {t('campaigns.analyticsHub.roasLifetimeNote', 'ROAS é acumulado desde o início da campanha — filtros de período não se aplicam (receita não é guardada por dia).')}
          </p>
        </div>
      )}

      {/* Device Breakdown (lifetime — providers não expõem breakdown por dispositivo filtrado) */}
      {metrics?.deviceBreakdown && Object.keys(metrics.deviceBreakdown).length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-6">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('campaigns.analyticsHub.deviceBreakdown', 'Desempenho por Dispositivo')}
            </h3>
            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 uppercase tracking-wide">
              {t('common.lifetime', 'Lifetime')}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(metrics.deviceBreakdown).map(([device, data]: [string, any]) => (
              <div key={device} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-gray-600 dark:text-gray-400">
                    {deviceIcons[device] || <Monitor className="w-5 h-5" />}
                  </div>
                  <p className="font-medium text-gray-900 dark:text-white">{deviceLabels[device] || device}</p>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('campaigns.metrics.impressions', 'Impressões')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{(data.impressions || 0).toLocaleString('pt-BR')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('campaigns.metrics.clicks', 'Cliques')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{(data.clicks || 0).toLocaleString('pt-BR')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('campaigns.metrics.spend', 'Gasto')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(data.spend || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('campaigns.metrics.conversions', 'Conversões')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{(data.conversions || 0).toLocaleString('pt-BR')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignAnalytics;
